import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { GetPartyResponse } from '../types/GetPartyResponse';
import { PartyDetailsListSliceState } from '../types/types';

const initialState: PartyDetailsListSliceState = {
  insuredOn: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 20,
    },
  },
  claimantOn: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 20,
    },
  },
  beneficiaryOn: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 20,
    },
  },
  isContactOf: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 20,
    },
  },
  hasContactsOf: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 20,
    },
  },

  dataRequestsStates: {
    insuredOnData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    claimantOnData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    beneficiaryOnData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    isContactOfData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
    hasContactsOfData: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
  },
};

const partyDetailsTabListSlice = createSlice({
  name: 'partyDetailsTabList',
  initialState,
  reducers: {
    getPartyDetailsInsuredOnListData: (_, _action: PayloadAction<{ id: string }>) => {},
    startLoadingPartyDetailsInsuredOnListData: (state) => {
      state.dataRequestsStates.insuredOnData = {
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    getPartyDetailsInsuredOnListDataSuccess: (
      state,
      { payload: { data, shouldRewrite = true } }: PayloadAction<{ data: GetPartyResponse; shouldRewrite?: boolean }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];
      if (shouldRewrite) {
        state.insuredOn.listings = listingsArray;
      } else {
        state.insuredOn.listings = [...state.insuredOn.listings, ...listingsArray];
      }
      state.insuredOn.pagination = data.pagination;
      state.dataRequestsStates.insuredOnData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getPartyDetailsInsuredOnListDataError: (state) => {
      state.dataRequestsStates.insuredOnData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },

    getPartyDetailsClaimantOnListData: (_, _action: PayloadAction<{ id: string }>) => {},
    startLoadingPartyDetailsClaimantOnListData: (state) => {
      state.dataRequestsStates.claimantOnData = {
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    getPartyDetailsClaimantOnListDataSuccess: (
      state,
      { payload: { data, shouldRewrite = true } }: PayloadAction<{ data: GetPartyResponse; shouldRewrite?: boolean }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];
      if (shouldRewrite) {
        state.claimantOn.listings = listingsArray;
      } else {
        state.claimantOn.listings = [...state.claimantOn.listings, ...listingsArray];
      }
      state.claimantOn.pagination = data.pagination;
      state.dataRequestsStates.claimantOnData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getPartyDetailsClaimantOnListDataError: (state) => {
      state.dataRequestsStates.claimantOnData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },

    getPartyDetailsBeneficiaryOnListData: (_, _action: PayloadAction<{ id: string }>) => {},
    startLoadingPartyDetailsBeneficiaryOnListData: (state) => {
      state.dataRequestsStates.beneficiaryOnData = {
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    getPartyDetailsBeneficiaryOnListDataSuccess: (
      state,
      { payload: { data, shouldRewrite = true } }: PayloadAction<{ data: GetPartyResponse; shouldRewrite?: boolean }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];
      if (shouldRewrite) {
        state.beneficiaryOn.listings = listingsArray;
      } else {
        state.beneficiaryOn.listings = [...state.beneficiaryOn.listings, ...listingsArray];
      }
      state.beneficiaryOn.pagination = data.pagination;
      state.dataRequestsStates.beneficiaryOnData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getPartyDetailsBeneficiaryOnListDataError: (state) => {
      state.dataRequestsStates.beneficiaryOnData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },

    getPartyDetailsIsContactOfListData: (_, _action: PayloadAction<{ id: string }>) => {},
    startLoadingPartyDetailsIsContactOfListData: (state) => {
      state.dataRequestsStates.isContactOfData = {
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    getPartyDetailsIsContactOfListDataSuccess: (
      state,
      { payload: { data, shouldRewrite = true } }: PayloadAction<{ data: GetPartyResponse; shouldRewrite?: boolean }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];
      if (shouldRewrite) {
        state.isContactOf.listings = listingsArray;
      } else {
        state.isContactOf.listings = [...state.isContactOf.listings, ...listingsArray];
      }
      state.isContactOf.pagination = data.pagination;
      state.dataRequestsStates.isContactOfData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getPartyDetailsIsContactOfListDataError: (state) => {
      state.dataRequestsStates.isContactOfData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },

    getPartyDetailsHasContactsOfListData: (_, _action: PayloadAction<{ id: string }>) => {},
    startLoadingPartyDetailsHasContactsOfListData: (state) => {
      state.dataRequestsStates.hasContactsOfData = {
        isLoading: true,
        isFetching: true,
        isError: false,
        isSuccess: false,
      };
    },
    getPartyDetailsHasContactsOfListDataSuccess: (
      state,
      { payload: { data, shouldRewrite = true } }: PayloadAction<{ data: GetPartyResponse; shouldRewrite?: boolean }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];
      if (shouldRewrite) {
        state.hasContactsOf.listings = listingsArray;
      } else {
        state.hasContactsOf.listings = [...state.hasContactsOf.listings, ...listingsArray];
      }
      state.hasContactsOf.pagination = data.pagination;
      state.dataRequestsStates.hasContactsOfData = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    getPartyDetailsHasContactsOfListDataError: (state) => {
      state.dataRequestsStates.hasContactsOfData = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },

    incrementPartyDetailsInsuredOnListPage: (state, _action: PayloadAction<{ id: string }>) => {
      state.insuredOn.pagination.page++;
    },
    incrementPartyDetailsClaimantOnListPage: (state, _action: PayloadAction<{ id: string }>) => {
      state.claimantOn.pagination.page++;
    },
    incrementPartyDetailsHasContactsOfListPage: (state, _action: PayloadAction<{ id: string }>) => {
      state.hasContactsOf.pagination.page++;
    },
    incrementPartyDetailsBeneficiaryOnListPage: (state, _action: PayloadAction<{ id: string }>) => {
      state.beneficiaryOn.pagination.page++;
    },
    incrementPartyDetailsIsContactOfListPage: (state, _action: PayloadAction<{ id: string }>) => {
      state.isContactOf.pagination.page++;
    },
  },
});

export const {
  getPartyDetailsInsuredOnListData,
  startLoadingPartyDetailsInsuredOnListData,
  getPartyDetailsInsuredOnListDataSuccess,
  getPartyDetailsInsuredOnListDataError,
  getPartyDetailsBeneficiaryOnListData,
  startLoadingPartyDetailsBeneficiaryOnListData,
  getPartyDetailsBeneficiaryOnListDataSuccess,
  getPartyDetailsBeneficiaryOnListDataError,
  getPartyDetailsClaimantOnListData,
  startLoadingPartyDetailsClaimantOnListData,
  getPartyDetailsClaimantOnListDataSuccess,
  getPartyDetailsClaimantOnListDataError,
  getPartyDetailsIsContactOfListData,
  startLoadingPartyDetailsIsContactOfListData,
  getPartyDetailsIsContactOfListDataSuccess,
  getPartyDetailsIsContactOfListDataError,
  getPartyDetailsHasContactsOfListData,
  getPartyDetailsHasContactsOfListDataSuccess,
  getPartyDetailsHasContactsOfListDataError,
  startLoadingPartyDetailsHasContactsOfListData,
  incrementPartyDetailsBeneficiaryOnListPage,
  incrementPartyDetailsClaimantOnListPage,
  incrementPartyDetailsHasContactsOfListPage,
  incrementPartyDetailsInsuredOnListPage,
  incrementPartyDetailsIsContactOfListPage,
} = partyDetailsTabListSlice.actions;

export const partyDetailsTabListReducer = partyDetailsTabListSlice.reducer;
export const partyDetailsTabListReducerPath = partyDetailsTabListSlice.reducerPath;
